import { match } from 'ts-pattern';
import { findIndexPage, Page } from '../utils';

export interface BreadcrumbItem {
  label: string;
  href: string;
}

const trim = (s: string) => s.replace(/^\/|\/$/g, '');

const slugify = (s: string) =>
  s
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '');

const humanize = (s: string) => s.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

/**
 * Returns the URL segments between the category folder and the current page.
 * The category and subcategory are rendered separately by the layout, so they
 * are stripped here to avoid duplication.
 *
 * The category's segments come from its resolved index page (via
 * `findIndexPage`) rather than slugifying the display name, because the name
 * doesn't always slugify to its folder — e.g. "Guides & Tools" → "guides",
 * "eIDs" → "e-ids" — so the resolved slug is the only reliable way to identify
 * which leading segments to drop.
 */
export function getBreadcrumbTrail(
  pathname: string,
  frontmatter?: { category?: string; subcategory?: string },
  navData?: { signaturesPages: Page[]; verifyPages: Page[] },
): BreadcrumbItem[] {
  const segments = trim(pathname).split('/').filter(Boolean);

  // Need product / ancestor / page for any intermediate crumb to exist.
  if (segments.length < 3) return [];

  const [product] = segments;
  const ancestors = segments.slice(1, -1);

  const pages = match(product)
    .with('signatures', () => navData?.signaturesPages ?? [])
    .with('verify', () => navData?.verifyPages ?? [])
    .otherwise(() => []);
  const categoryIndex = frontmatter?.category
    ? findIndexPage(frontmatter.category, pages)
    : undefined;
  const catSegs = categoryIndex?.fields?.slug
    ? trim(categoryIndex.fields.slug).split('/').slice(1)
    : [];
  const start = catSegs.length && catSegs.every((s, i) => ancestors[i] === s) ? catSegs.length : 0;

  const subSlug = frontmatter?.subcategory && slugify(frontmatter.subcategory);
  const end =
    subSlug && ancestors[ancestors.length - 1] === subSlug
      ? ancestors.length - 1
      : ancestors.length;

  return ancestors.slice(start, end).map((segment, i) => ({
    label: humanize(segment),
    href: `/${product}/${ancestors.slice(0, start + i + 1).join('/')}`,
  }));
}
